import React from 'react';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import { toastError, toastSuccess } from '@/lib/toast';
import Icon from '@mdi/react';
import { mdiPlusBoxOutline, mdiAlertOutline } from '@mdi/js';

import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import PageHeader from '@/components/layout/PageHeader';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Grid, { GridItem } from '@/components/layout/Grid';
import AlertBox from '@/components/layout/AlertBox';
import CodeBlock from '@/components/layout/CodeBlock';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Modal from '@/components/layout/Modal';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoMultiSeletor from '@/components/Formulario/CampoMultiSeletor';
import useWindowSize from '@/lib/useWindowSize';
import { getInstallBaseUrl } from '@/lib/tenant';
import { extractApiError } from '@/lib/apiError';

// Só tipos: o vite não resolve import de runtime de @webip/shared.
import type { McpTokenDTO, McpTokenCriadoDTO } from '@webip/shared/src/dto/mcp';
import type { ApplianceDTO } from '@webip/shared/src/dto/appliance';

const statusMeta: Record<McpTokenDTO['status'], { label: string; color: string; background: string }> = {
    ativo: { label: 'Ativo', color: 'var(--tc-text-color-success-emphasis)', background: 'var(--tc-background-color-success-subtle)' },
    expirado: { label: 'Expirado', color: 'var(--tc-color-gray-700)', background: 'var(--tc-color-gray-200)' },
    revogado: { label: 'Revogado', color: 'var(--tc-text-color-danger-emphasis)', background: 'var(--tc-background-color-danger-subtle)' },
};

const Pill = ({ label, color, background }: { label: string; color: string; background: string }) => (
    <span
        style={{
            fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '999px',
            color, backgroundColor: background, whiteSpace: 'nowrap',
        }}
    >
        {label}
    </span>
);

/** "há 3 h", "em 12 d" — mesma leitura da tela de agentes; o absoluto vai no `title`. */
function formatDistancia(value?: string | null): string {
    if (!value) return '—';
    const diffMs = new Date(value).getTime() - Date.now();
    const futuro = diffMs > 0;
    const min = Math.round(Math.abs(diffMs) / 60_000);
    if (min < 1) return futuro ? 'em instantes' : 'agora';
    const texto = min < 60
        ? `${min} min`
        : min < 60 * 24
            ? `${Math.round(min / 60)} h`
            : `${Math.round(min / (60 * 24))} d`;
    return futuro ? `em ${texto}` : `há ${texto}`;
}

function formatAbsoluto(value?: string | null): string | undefined {
    return value ? new Date(value).toLocaleString() : undefined;
}

const OPCOES_VALIDADE = [
    { id: '', label: 'Não expira' },
    { id: '30', label: '30 dias' },
    { id: '90', label: '90 dias' },
    { id: '365', label: '1 ano' },
];

/**
 * A URL do endpoint segue a forma de acesso atual: sob subdomínio o tenant já
 * está no host, no formato legado ele precisa continuar no path — mesma regra
 * dos instaladores de uma linha (`getInstallBaseUrl`).
 */
const urlEndpoint = () => `${getInstallBaseUrl()}/api/mcp`;

const configCliente = (token: string) =>
    JSON.stringify(
        {
            mcpServers: {
                webip: {
                    type: 'http',
                    url: urlEndpoint(),
                    headers: { Authorization: `Bearer ${token}` },
                },
            },
        },
        null,
        2,
    );

const Page = () => {
    const [, , isMobile] = useWindowSize();
    const [mostrarEncerrados, setMostrarEncerrados] = React.useState(false);
    const [criando, setCriando] = React.useState(false);
    const [gerado, setGerado] = React.useState<McpTokenCriadoDTO | null>(null);

    const [nome, setNome] = React.useState('');
    const [usuarioId, setUsuarioId] = React.useState<string | null>(null);
    const [applianceIds, setApplianceIds] = React.useState<string[]>([]);
    const [applianceId, setApplianceId] = React.useState<string | null>(null);
    const [validade, setValidade] = React.useState<string | null>('');
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});

    const queryTokens = useQuery({
        queryKey: ['mcp-tokens', mostrarEncerrados],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: McpTokenDTO[] }>(
                `/api/mcp_token${mostrarEncerrados ? '?encerrados=1' : ''}`,
                { withCredentials: true },
            );
            return res.data.result;
        },
    });

    const queryUsuarios = useQuery({
        queryKey: ['mcp-usuarios'],
        queryFn: async () => {
            const res = await axios.get<{ result: { id: number; nome: string }[] }>(
                '/api/usuario/box',
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
    });

    const queryAppliances = useQuery({
        queryKey: ['mcp-appliances'],
        queryFn: async () => {
            const res = await axios.get<{ result: ApplianceDTO[] }>('/api/appliance', {
                withCredentials: true,
            });
            return res.data.result ?? [];
        },
    });

    const fecharCriacao = () => {
        setCriando(false);
        setNome('');
        setUsuarioId(null);
        setApplianceIds([]);
        setApplianceId(null);
        setValidade('');
        setFieldErrors({});
    };

    const mutCriar = useMutation({
        mutationFn: async () => {
            const res = await axios.post<{ result: McpTokenCriadoDTO }>(
                '/api/mcp_token',
                {
                    nome,
                    usuario_id: usuarioId ? Number(usuarioId) : undefined,
                    appliance_ids: applianceIds.length ? applianceIds.map(Number) : undefined,
                    appliance_id: applianceId ? Number(applianceId) : undefined,
                    ttl_dias: validade ? Number(validade) : undefined,
                },
                { withCredentials: true },
            );
            return res.data.result;
        },
        onSuccess: (result) => {
            fecharCriacao();
            setGerado(result);
            queryTokens.refetch();
        },
        onError: (error: unknown) => {
            const { message, fieldErrors: errs } = extractApiError(error, 'Erro ao gerar o token');
            if (errs) setFieldErrors(errs);
            toastError(message);
        },
    });

    const mutRevogar = useMutation({
        mutationFn: async (id: number) => axios.delete(`/api/mcp_token/${id}`, { withCredentials: true }),
        onSuccess: () => {
            queryTokens.refetch();
            toastSuccess('Token revogado. Quem estiver usando perde o acesso na próxima chamada.');
        },
        onError: (error: unknown) => {
            const { message } = extractApiError(error, 'Erro ao revogar o token');
            toastError(message);
        },
    });

    const opcoesUsuario = React.useMemo(
        () => (queryUsuarios.data ?? []).map(u => ({ id: String(u.id), label: u.nome })),
        [queryUsuarios.data],
    );

    const opcoesAppliance = React.useMemo(
        () => (queryAppliances.data ?? []).map(a => ({ id: String(a.id), label: a.nome ?? `#${a.id}` })),
        [queryAppliances.data],
    );

    const nomeAppliance = React.useCallback(
        (id: number) => (queryAppliances.data ?? []).find(a => a.id === id)?.nome ?? `#${id}`,
        [queryAppliances.data],
    );

    // Um padrão fora da allowlist seria recusado pelo backend — a lista de
    // opções já encolhe junto para o operador não chegar lá.
    const opcoesPadrao = applianceIds.length
        ? opcoesAppliance.filter(o => applianceIds.includes(o.id))
        : opcoesAppliance;

    const headers: DataTableHeader[] = [
        {
            column: 'nome',
            label: 'Token',
            mobileOrder: 0,
            render: (row: McpTokenDTO) => {
                const meta = statusMeta[row.status];
                return (
                    <VBox style={{ gap: '4px', alignItems: 'flex-start' }}>
                        <HBox style={{ gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600 }}>{row.nome}</span>
                            <Pill label={meta.label} color={meta.color} background={meta.background} />
                        </HBox>
                        <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--tc-color-gray-600)' }}>
                            {row.token_prefixo}…
                        </span>
                    </VBox>
                );
            },
        },
        {
            column: 'usuario_nome',
            label: 'Acessa como',
            mobileOrder: 1,
            render: (row: McpTokenDTO) => (
                <VBox style={{ gap: '2px' }}>
                    <span>{row.usuario_nome ?? `#${row.usuario_id}`}</span>
                    <span style={{ fontSize: '11px', color: 'var(--tc-color-gray-600)' }}>
                        {row.appliance_nome ? `padrão: ${row.appliance_nome}` : 'sem appliance padrão'}
                    </span>
                </VBox>
            ),
        },
        {
            column: 'appliance_ids',
            label: 'Appliances visíveis',
            mobileOrder: 2,
            render: (row: McpTokenDTO) => (
                row.appliance_ids?.length ? (
                    <VBox style={{ gap: '2px' }}>
                        {row.appliance_ids.map(id => (
                            <span key={id} style={{ fontSize: '12px' }}>{nomeAppliance(id)}</span>
                        ))}
                    </VBox>
                ) : (
                    <span
                        style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}
                        title="O escopo de appliances do grupo do usuário continua valendo."
                    >
                        Todas as do usuário
                    </span>
                )
            ),
        },
        {
            column: 'ultimo_uso_em',
            label: 'Último uso',
            mobileOrder: 3,
            render: (row: McpTokenDTO) => (
                <span title={formatAbsoluto(row.ultimo_uso_em)}>
                    {row.ultimo_uso_em ? formatDistancia(row.ultimo_uso_em) : 'nunca usado'}
                </span>
            ),
        },
        {
            column: 'expira_em',
            label: 'Expira',
            mobileOrder: 4,
            render: (row: McpTokenDTO) => (
                <span title={formatAbsoluto(row.expira_em)}>
                    {row.expira_em ? formatDistancia(row.expira_em) : 'não expira'}
                </span>
            ),
        },
        {
            column: 'data_criacao',
            label: 'Criado',
            mobileOrder: 5,
            render: (row: McpTokenDTO) => (
                <VBox style={{ gap: '2px' }}>
                    <span title={formatAbsoluto(row.data_criacao)}>{formatDistancia(row.data_criacao)}</span>
                    <span style={{ fontSize: '11px', color: 'var(--tc-color-gray-600)' }}>
                        {row.criado_por_nome ?? 'origem desconhecida'}
                    </span>
                </VBox>
            ),
        },
        {
            column: 'acoes',
            label: 'Ações',
            mobileOrder: 6,
            alignRight: true,
            render: (row: McpTokenDTO) => (
                row.status === 'ativo' ? (
                    <BotaoPerigoso
                        size="sm"
                        variant="outline-danger"
                        title="Revogar token"
                        confirmation={`Revogar o token "${row.nome}"? Todo cliente configurado com ele perde o acesso imediatamente, e não dá para reativá-lo.`}
                        onConfirm={() => mutRevogar.mutate(row.id)}
                    >
                        Revogar
                    </BotaoPerigoso>
                ) : (
                    <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>—</span>
                )
            ),
        },
    ];

    return (
        <LayoutUsuario>
            <VBox style={{ padding: isMobile ? '16px' : '24px', gap: '16px', height: '100%', overflow: 'auto' }}>
                <PageHeader
                    title="Acesso MCP"
                    description="Tokens que permitem a assistentes de IA externos consultar a configuração de rede desta conta pelo protocolo MCP."
                    actions={(
                        <Botao variant="primary" icon={mdiPlusBoxOutline} onClick={() => setCriando(true)}>
                            Novo token
                        </Botao>
                    )}
                />

                <AlertBox variant="info">
                    <VBox style={{ gap: '8px', alignItems: 'flex-start' }}>
                        <span>
                            O endpoint expõe <strong>somente leitura</strong>: listar appliances, redes LAN e WAN,
                            VPNs, IPSec, NAT, redirecionamentos, hotspots e grupos RADIUS, além de ping,
                            traceroute e teste HTTP rodados pela appliance. Senhas e chaves voltam mascaradas.
                        </span>
                        <span>
                            Nada é alterado por aqui — mudanças de configuração continuam passando pelo assistente
                            do painel, que pede confirmação antes de aplicar.
                        </span>
                    </VBox>
                </AlertBox>

                <VBox style={{ gap: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600 }}>Endereço do servidor MCP</span>
                    <CodeBlock value={urlEndpoint()} />
                </VBox>

                <HBox style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ fontSize: '16px', fontWeight: 600 }}>Tokens</span>
                    <Botao variant="none-secondary" size="sm" onClick={() => setMostrarEncerrados(v => !v)}>
                        {mostrarEncerrados ? 'Mostrar só os ativos' : 'Mostrar encerrados também'}
                    </Botao>
                </HBox>

                {queryTokens.isError ? (
                    <AlertBox variant="danger">Erro ao carregar os tokens.</AlertBox>
                ) : (queryTokens.data?.length ?? 0) === 0 && !queryTokens.isFetching ? (
                    <span style={{ fontSize: '13px', color: 'var(--tc-color-gray-600)' }}>
                        {mostrarEncerrados ? 'Nenhum token registrado.' : 'Nenhum token ativo no momento.'}
                    </span>
                ) : (
                    <DataTable isLoading={queryTokens.isFetching} cabecalhos={headers} linhas={queryTokens.data ?? []} />
                )}
            </VBox>

            <Modal
                instanceId="modal-mcp-token-novo"
                size="md"
                title="Novo token de acesso MCP"
                show={criando}
                onClose={fecharCriacao}
                onCancel={fecharCriacao}
                onConfirm={() => mutCriar.mutate()}
                confirmProps={{ label: 'Gerar token', disabled: !nome.trim() || mutCriar.isLoading }}
            >
                <VBox style={{ gap: '12px' }}>
                    <Grid>
                        <GridItem sm={12}>
                            <CampoTexto
                                label="Nome"
                                required
                                placeholder="Ex: Claude Desktop do NOC"
                                hint="Identifica este token na lista — o valor gerado não volta a aparecer."
                                value={nome}
                                onChange={setNome}
                                error={fieldErrors.nome}
                            />
                        </GridItem>
                        <GridItem sm={12} md={6}>
                            <CampoSeletor
                                nullable
                                label="Acessa como"
                                placeholder="(Você mesmo)"
                                hint="Identidade que as consultas assumem na trilha de auditoria."
                                value={usuarioId}
                                onChange={setUsuarioId}
                                options={opcoesUsuario}
                                error={fieldErrors.usuario_id}
                            />
                        </GridItem>
                        <GridItem sm={12} md={6}>
                            <CampoSeletor
                                noSearch
                                label="Validade"
                                value={validade}
                                onChange={setValidade}
                                options={OPCOES_VALIDADE}
                            />
                        </GridItem>
                        <GridItem sm={12}>
                            <CampoMultiSeletor
                                label="Appliances visíveis"
                                placeholder="(Todas as que o usuário já vê)"
                                value={applianceIds}
                                onChange={(ids: string[]) => {
                                    setApplianceIds(ids);
                                    // O padrão precisa continuar dentro da lista.
                                    if (applianceId && ids.length && !ids.includes(applianceId)) {
                                        setApplianceId(null);
                                    }
                                }}
                                options={opcoesAppliance}
                                error={fieldErrors.appliance_ids}
                            />
                            <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                                Deixe vazio para o token enxergar tudo que o usuário escolhido já enxerga.
                                O escopo do grupo desse usuário continua valendo por cima — o token
                                restringe, nunca amplia.
                            </span>
                        </GridItem>
                        <GridItem sm={12}>
                            <CampoSeletor
                                nullable
                                label="Appliance padrão"
                                placeholder="(Nenhuma)"
                                hint="Usada quando a consulta não disser qual appliance quer."
                                value={applianceId}
                                onChange={setApplianceId}
                                options={opcoesPadrao}
                                error={fieldErrors.appliance_id}
                            />
                        </GridItem>
                    </Grid>
                </VBox>
            </Modal>

            <Modal
                instanceId="modal-mcp-token-gerado"
                size="md"
                title="Token gerado"
                show={!!gerado}
                closeable={false}
                ignoreOverlayClick
                onClose={() => setGerado(null)}
                onCancel={() => setGerado(null)}
                cancelProps={{ label: 'Já copiei, fechar' }}
                confirmProps={{ visible: false }}
            >
                {gerado && (
                    <VBox style={{ gap: '12px' }}>
                        <AlertBox variant="warning">
                            <HBox style={{ gap: '8px', alignItems: 'flex-start' }}>
                                <Icon path={mdiAlertOutline} size="20px" />
                                <span>
                                    Copie agora. Este valor não fica guardado e não há como recuperá-lo depois —
                                    quem perder precisa gerar outro token.
                                </span>
                            </HBox>
                        </AlertBox>

                        <VBox style={{ gap: '6px' }}>
                            <span style={{ fontSize: '13px', fontWeight: 600 }}>Token</span>
                            <CodeBlock value={gerado.token} />
                        </VBox>

                        <VBox style={{ gap: '6px' }}>
                            <span style={{ fontSize: '13px', fontWeight: 600 }}>Configuração do cliente MCP</span>
                            <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                                Cole no arquivo de servidores MCP do cliente (por exemplo, <code>.mcp.json</code>).
                            </span>
                            <CodeBlock value={configCliente(gerado.token)} />
                        </VBox>

                        {gerado.expira_em && (
                            <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                                Expira em {new Date(gerado.expira_em).toLocaleString()}.
                            </span>
                        )}
                    </VBox>
                )}
            </Modal>
        </LayoutUsuario>
    );
};

export default Page;
